<script setup lang="ts">
/**
 * TutorialSpotlight Component
 *
 * Creates a dark overlay with a "spotlight" cutout around a target element.
 * Uses SVG with a mask to create the transparent hole effect.
 */

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import type { TutorialTarget, SpotlightRect } from '@/types/tutorial'

const props = withDefaults(
  defineProps<{
    /** Target element specification */
    target: TutorialTarget | null
    /** Padding around the highlighted element (px) */
    padding?: number
    /** Border radius of the cutout (px) */
    borderRadius?: number
  }>(),
  {
    padding: 8,
    borderRadius: 8
  }
)

const emit = defineEmits<{
  /** Emitted when target element is found/updated */
  targetFound: [element: HTMLElement]
  /** Emitted when target element cannot be found */
  targetNotFound: []
}>()

// Viewport dimensions (reactive for template binding)
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1920)
const viewportHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 1080)

// Cutout rectangle dimensions
const cutout = ref<SpotlightRect>({
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  borderRadius: 8
})

// Whether we have a valid cutout to show
const showCutout = ref(false)

// ResizeObserver for viewport changes
let resizeObserver: ResizeObserver | null = null
let targetElement: HTMLElement | null = null

/**
 * Find the target element using selectors
 */
function findTargetElement(): HTMLElement | null {
  if (!props.target) return null

  // Try data attribute first
  if (props.target.dataAttribute) {
    const el = document.querySelector(
      `[data-tutorial="${props.target.dataAttribute}"]`
    ) as HTMLElement | null
    if (el) return el
  }

  // Try primary selector
  const el = document.querySelector(props.target.selector) as HTMLElement | null
  if (el) return el

  // Try fallback selector
  if (props.target.fallbackSelector) {
    return document.querySelector(
      props.target.fallbackSelector
    ) as HTMLElement | null
  }

  return null
}

/**
 * Update cutout position based on target element
 */
function updateCutout(): void {
  if (!props.target) {
    showCutout.value = false
    return
  }

  const element = findTargetElement()

  if (!element) {
    showCutout.value = false
    emit('targetNotFound')
    return
  }

  targetElement = element
  const rect = element.getBoundingClientRect()

  cutout.value = {
    x: rect.left - props.padding,
    y: rect.top - props.padding,
    width: rect.width + props.padding * 2,
    height: rect.height + props.padding * 2,
    borderRadius: props.borderRadius
  }

  showCutout.value = true
  emit('targetFound', element)
}

/**
 * Handle scroll events to update position
 */
function handleScroll(): void {
  if (showCutout.value) {
    updateCutout()
  }
}

/**
 * Handle resize events
 */
function handleResize(): void {
  // Update viewport dimensions
  viewportWidth.value = window.innerWidth
  viewportHeight.value = window.innerHeight

  if (showCutout.value) {
    updateCutout()
  }
}

// Watch for target changes
watch(
  () => props.target,
  () => {
    // Small delay to let DOM settle
    setTimeout(updateCutout, 50)
  },
  { immediate: true }
)

onMounted(() => {
  // Set up resize observer
  resizeObserver = new ResizeObserver(handleResize)
  resizeObserver.observe(document.body)

  // Listen for scroll events
  window.addEventListener('scroll', handleScroll, true)
  window.addEventListener('resize', handleResize)

  // Initial update
  updateCutout()
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  window.removeEventListener('scroll', handleScroll, true)
  window.removeEventListener('resize', handleResize)
})

// Expose cutout for tooltip positioning
defineExpose({
  cutout,
  showCutout,
  updateCutout
})
</script>

<template>
  <svg
    class="tutorial-spotlight"
    :viewBox="`0 0 ${viewportWidth} ${viewportHeight}`"
    preserveAspectRatio="none"
  >
    <defs>
      <mask id="spotlight-mask">
        <!-- White background = visible overlay -->
        <rect width="100%" height="100%" fill="white" />
        <!-- Black cutout = transparent hole -->
        <rect
          v-if="showCutout"
          :x="cutout.x"
          :y="cutout.y"
          :width="cutout.width"
          :height="cutout.height"
          :rx="cutout.borderRadius"
          :ry="cutout.borderRadius"
          fill="black"
        />
      </mask>
    </defs>

    <!-- Dark overlay with cutout -->
    <rect
      width="100%"
      height="100%"
      class="spotlight-overlay"
      mask="url(#spotlight-mask)"
    />

    <!-- Glowing border around cutout -->
    <rect
      v-if="showCutout"
      :x="cutout.x"
      :y="cutout.y"
      :width="cutout.width"
      :height="cutout.height"
      :rx="cutout.borderRadius"
      :ry="cutout.borderRadius"
      fill="none"
      class="spotlight-border"
    />
  </svg>
</template>

<style scoped>
.tutorial-spotlight {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 10001;
}

.spotlight-overlay {
  fill: rgba(0, 0, 0, 0.75);
  transition: fill 0.3s ease;
}

.spotlight-border {
  stroke: rgba(232, 197, 71, 0.6);
  stroke-width: 2;
  filter: drop-shadow(0 0 8px rgba(232, 197, 71, 0.4));
  animation: spotlight-pulse 2s ease-in-out infinite;
}

@keyframes spotlight-pulse {
  0%,
  100% {
    stroke: rgba(232, 197, 71, 0.6);
    filter: drop-shadow(0 0 8px rgba(232, 197, 71, 0.4));
  }
  50% {
    stroke: rgba(232, 197, 71, 0.9);
    filter: drop-shadow(0 0 16px rgba(232, 197, 71, 0.6));
  }
}
</style>
